import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { fmtN, fmtPct, pctVal } from "../../lib/funnel/helpers";
import { PRIMARY, FG1, FG3, MUTED, GOLD } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import { InfoButton } from "../../lib/funnel/InfoButton";
import type { FilterPillSpec } from "../../components/FilterPills";
import type { BaselineRow } from "./sql";
import { CONV_FEATURES } from "./helpers";
import type { HeatmapFeature } from "./helpers";

// End-state feature → paid lift table. For each feature row (and 2+/5+ child
// thresholds) we compare paid conversion among users who ever touched the
// feature vs users who never did, plus lift vs the cohort-wide baseline. No
// week buckets — everything is "by today, end of cohort window".

type Row = {
  feature: HeatmapFeature;
  depth: number;
  reachers: number;
  reachersPaid: number;
  nonReachers: number;
  nonReachersPaid: number;
};

type SortKey = "feature" | "activated" | "activatedPaid" | "nonActivatedPaid" | "lift";

// Subtle teal wash on the active sort column, matching the SignupFunnel B1 panel.
const ACTIVE_COL_BG = "rgba(47, 122, 138, 0.06)";

function compute(users: BaselineRow[], feature: HeatmapFeature, depth: number): Row {
  let reachers = 0;
  let reachersPaid = 0;
  let nonReachers = 0;
  let nonReachersPaid = 0;
  for (const u of users) {
    const hit = feature.eventDate(u) != null;
    const paid = u.paid_converted_date_utc != null;
    if (hit) {
      reachers += 1;
      if (paid) reachersPaid += 1;
    } else {
      nonReachers += 1;
      if (paid) nonReachersPaid += 1;
    }
  }
  return { feature, depth, reachers, reachersPaid, nonReachers, nonReachersPaid };
}

export function FeatureConversionTable({
  users,
  pagePills,
  compareUsers,
  comparePills,
}: {
  users: BaselineRow[];
  pagePills?: FilterPillSpec[];
  compareUsers?: BaselineRow[] | null;
  comparePills?: FilterPillSpec[];
}) {
  const compareOn = !!compareUsers;
  const [sortKey, setSortKey] = useState<SortKey>("feature");
  const [view, setView] = useState<"pct" | "abs">("pct");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set());
  const [rowsMenuOpen, setRowsMenuOpen] = useState(false);
  const rowsMenuRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const [tooltip, setTooltip] = useState<{
    x: number; y: number; lede: string; body: string; sub: string;
  } | null>(null);

  useEffect(() => {
    if (!rowsMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (rowsMenuRef.current && !rowsMenuRef.current.contains(e.target as Node)) setRowsMenuOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [rowsMenuOpen]);

  const visibleFeatures = useMemo(
    () => CONV_FEATURES.filter((f) => !hiddenRows.has(f.key)),
    [hiddenRows],
  );

  const total = users.length;
  const totalPaid = useMemo(() => users.filter((u) => u.paid_converted_date_utc != null).length, [users]);
  const baselinePct = pctVal(totalPaid, total);

  const parentRows = useMemo(
    () => visibleFeatures.map((f) => compute(users, f, 0)),
    [users, visibleFeatures],
  );
  const compareParentRows = useMemo(
    () => compareUsers ? visibleFeatures.map((f) => compute(compareUsers, f, 0)) : null,
    [compareUsers, visibleFeatures],
  );
  const childRows = useMemo(() => {
    const out: Record<string, Row[]> = {};
    for (const parent of visibleFeatures) {
      if (!parent.children) continue;
      out[parent.key] = parent.children.map((c) => compute(users, c, 1));
    }
    return out;
  }, [users, visibleFeatures]);
  const compareChildRows = useMemo(() => {
    if (!compareUsers) return null;
    const out: Record<string, Row[]> = {};
    for (const parent of visibleFeatures) {
      if (!parent.children) continue;
      out[parent.key] = parent.children.map((c) => compute(compareUsers, c, 1));
    }
    return out;
  }, [compareUsers, visibleFeatures]);
  const compareByKey = useMemo(() => {
    const m = new Map<string, Row>();
    if (compareParentRows) for (const r of compareParentRows) m.set(r.feature.key, r);
    if (compareChildRows) for (const arr of Object.values(compareChildRows)) for (const r of arr) m.set(r.feature.key, r);
    return m;
  }, [compareParentRows, compareChildRows]);

  const sortedParents = useMemo(() => {
    const arr = [...parentRows];
    const cmp = (a: Row, b: Row): number => {
      const aReachPct = pctVal(a.reachersPaid, a.reachers);
      const bReachPct = pctVal(b.reachersPaid, b.reachers);
      const aNonPct = pctVal(a.nonReachersPaid, a.nonReachers);
      const bNonPct = pctVal(b.nonReachersPaid, b.nonReachers);
      const aLift = aNonPct === 0 ? 0 : aReachPct / aNonPct;
      const bLift = bNonPct === 0 ? 0 : bReachPct / bNonPct;
      switch (sortKey) {
        case "feature": {
          const order = CONV_FEATURES.map((f) => f.key);
          return order.indexOf(a.feature.key) - order.indexOf(b.feature.key);
        }
        case "activated": return b.reachers - a.reachers;
        case "activatedPaid": return bReachPct - aReachPct;
        case "nonActivatedPaid": return bNonPct - aNonPct;
        case "lift": return bLift - aLift;
      }
    };
    arr.sort(cmp);
    return arr;
  }, [parentRows, sortKey, baselinePct, total]);

  // Max lift across parent rows; child lifts scale into the same ramp so a 2x
  // child reads weaker than a 2x parent (matches the heatmap's parent/child
  // colour split).
  const maxLift = useMemo(() => {
    let max = 1;
    for (const r of parentRows) {
      const p = pctVal(r.reachersPaid, r.reachers);
      const n = pctVal(r.nonReachersPaid, r.nonReachers);
      const l = n === 0 ? 0 : p / n;
      if (Number.isFinite(l) && l > max) max = l;
    }
    return max;
  }, [parentRows]);

  const PRIMARY_RGB: [number, number, number] = useMemo(() => {
    const m = PRIMARY.replace("#", "").match(/.{2}/g) ?? ["2F", "7A", "8A"];
    return [parseInt(m[0] ?? "00", 16), parseInt(m[1] ?? "00", 16), parseInt(m[2] ?? "00", 16)];
  }, []);
  const GREY_RGB: [number, number, number] = [90, 90, 90];

  function liftFill(lift: number, isChild: boolean): string | undefined {
    if (lift <= 0) return undefined;
    if (lift < 1) {
      // Below baseline — orange-tinted, fixed mild alpha.
      return isChild ? "rgba(220, 106, 76, 0.08)" : "rgba(220, 106, 76, 0.14)";
    }
    const range = Math.max(0.0001, maxLift - 1);
    const intensity = Math.min(1, (lift - 1) / range);
    const [r, g, b] = isChild ? GREY_RGB : PRIMARY_RGB;
    const alpha = isChild ? 0.04 + intensity * 0.28 : 0.10 + intensity * 0.55;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function renderConvValue(numerator: number, denom: number) {
    if (denom === 0) return <span style={{ color: MUTED }}>-</span>;
    const pct = numerator / denom;
    if (view === "pct") {
      return <>{Math.round(pct * 100)}%</>;
    }
    return (
      <>
        {fmtN(numerator)}
        <span style={{ color: FG3, marginLeft: 6, fontSize: 11 }}>
          of {fmtN(denom)}
        </span>
      </>
    );
  }

  function renderDualValue(
    baseNum: number, baseDenom: number,
    compNum: number | null, compDenom: number | null,
    isAbsoluteCount: boolean,
  ) {
    const formatPctOrCount = (num: number, denom: number) => {
      if (denom === 0) return null;
      if (isAbsoluteCount) {
        return view === "pct" ? `${Math.round((num / denom) * 100)}%` : fmtN(num);
      }
      if (view === "pct") return `${Math.round((num / denom) * 100)}%`;
      return fmtN(num);
    };
    const baseStr = formatPctOrCount(baseNum, baseDenom);
    if (!compareOn) {
      if (baseStr == null) return <span style={{ color: MUTED }}>-</span>;
      if (!isAbsoluteCount && view === "abs") {
        return renderConvValue(baseNum, baseDenom);
      }
      return <>{baseStr}</>;
    }
    const compStr = formatPctOrCount(compNum ?? 0, compDenom ?? 0);
    return (
      <span style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "baseline", columnGap: 6, whiteSpace: "nowrap" }}>
        <span style={{ color: PRIMARY, fontWeight: 600, textAlign: "right" }}>{baseStr ?? "-"}</span>
        <span style={{ color: FG3, fontSize: 11 }}>|</span>
        <span style={{ color: GOLD, fontWeight: 600, textAlign: "left" }}>{compStr ?? "-"}</span>
      </span>
    );
  }

  function renderDualLift(baseLift: number, baseLiftDefined: boolean, compLift: number, compLiftDefined: boolean) {
    const liftStr = (val: number, defined: boolean) => defined ? `${val.toFixed(2)}×` : "-";
    if (!compareOn) {
      return baseLiftDefined ? <>{baseLift.toFixed(2)}×</> : <span style={{ color: MUTED }}>-</span>;
    }
    return (
      <span style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "baseline", columnGap: 6, whiteSpace: "nowrap" }}>
        <span style={{ color: PRIMARY, textAlign: "right" }}>{liftStr(baseLift, baseLiftDefined)}</span>
        <span style={{ color: FG3, fontSize: 11 }}>|</span>
        <span style={{ color: GOLD, textAlign: "left" }}>{liftStr(compLift, compLiftDefined)}</span>
      </span>
    );
  }

  function renderRow(row: Row, isFirstParent: boolean) {
    const hasChildren = row.depth === 0 && (row.feature.children?.length ?? 0) > 0;
    const isExpanded = expanded.has(row.feature.key);
    const anyExpanded = expanded.size > 0;

    const reachPct = pctVal(row.reachersPaid, row.reachers);
    const nonPct = pctVal(row.nonReachersPaid, row.nonReachers);
    const lift = nonPct === 0 ? 0 : reachPct / nonPct;
    const liftDefined = row.reachers > 0 && row.nonReachers > 0 && nonPct > 0;

    const compRow = compareByKey.get(row.feature.key) ?? null;
    const compReachPct = compRow ? pctVal(compRow.reachersPaid, compRow.reachers) : 0;
    const compNonPct = compRow ? pctVal(compRow.nonReachersPaid, compRow.nonReachers) : 0;
    const compLift = compRow && compNonPct > 0 ? compReachPct / compNonPct : 0;
    const compLiftDefined = !!compRow && compRow.reachers > 0 && compRow.nonReachers > 0 && compNonPct > 0;
    const compTotal = (compRow ? compRow.reachers + compRow.nonReachers : 0);

    const isChild = row.depth > 0;
    const labelColor = isChild ? FG3 : FG1;
    const labelWeight = isChild ? 500 : 700;
    const labelSize = isChild ? 12 : 13;
    const cellPad = "10px 12px";
    const rowBg = isChild ? "var(--bg-1)" : "var(--bg-2, #f5f4f2)";

    const cellBg = (col: SortKey): string => sortKey === col
      ? `linear-gradient(${ACTIVE_COL_BG}, ${ACTIVE_COL_BG}), ${rowBg}`
      : rowBg;

    const actionPhrase = row.feature.label.toLowerCase();
    const buildTooltip = (col: SortKey) => {
      switch (col) {
        case "activated":
          return {
            lede: `${Math.round((row.reachers / Math.max(1, total)) * 100)}% of the cohort ever ${actionPhrase}.`,
            body: `Of ${fmtN(total)} users in the baseline cohort, ${fmtN(row.reachers)} touched this feature at least once.`,
            sub: `${fmtN(row.reachers)} / ${fmtN(total)} users`,
          };
        case "activatedPaid":
          if (row.reachers === 0) return null;
          return {
            lede: `${Math.round(reachPct)}% of users who ${actionPhrase} are on a paid plan today.`,
            body: `Among the ${fmtN(row.reachers)} activated users (touched this feature), ${fmtN(row.reachersPaid)} are paid.`,
            sub: `${fmtN(row.reachersPaid)} / ${fmtN(row.reachers)} activated users`,
          };
        case "nonActivatedPaid":
          if (row.nonReachers === 0) return null;
          return {
            lede: `${Math.round(nonPct)}% of users who never ${actionPhrase} are on a paid plan today.`,
            body: `Among the ${fmtN(row.nonReachers)} non-activated users (never touched this feature), ${fmtN(row.nonReachersPaid)} are paid.`,
            sub: `${fmtN(row.nonReachersPaid)} / ${fmtN(row.nonReachers)} non-activated users`,
          };
        case "lift":
          if (!liftDefined) {
            return {
              lede: `Lift can't be computed for this row.`,
              body: `Needs at least one activated and one non-activated paid user. Try a wider lookback or different filters.`,
              sub: "",
            };
          }
          return {
            lede: lift >= 1
              ? `Activated users are ${lift.toFixed(2)}× as likely to be paid as non-activated.`
              : `Activated users are ${(1 / lift).toFixed(2)}× less likely to be paid than non-activated.`,
            body: `Activated paid %: ${Math.round(reachPct)}% · Non-activated paid %: ${Math.round(nonPct)}%. Correlation, not causation — paid users tend to use more features.`,
            sub: `${fmtN(row.reachersPaid)} / ${fmtN(row.reachers)} vs ${fmtN(row.nonReachersPaid)} / ${fmtN(row.nonReachers)}`,
          };
        default:
          return null;
      }
    };

    const cellHover = (col: SortKey) => {
      const t = buildTooltip(col);
      if (!t) return {};
      return {
        onMouseMove: (e: React.MouseEvent) => {
          const rect = cardRef.current?.getBoundingClientRect();
          if (!rect) return;
          setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, ...t });
        },
        onMouseLeave: () => setTooltip(null),
      };
    };

    return (
      <Fragment key={(row.depth === 0 ? "" : "child-") + row.feature.key}>
        {row.depth === 0 && !isFirstParent && anyExpanded && (
          <tr aria-hidden="true">
            <td colSpan={5} style={{ padding: 0, height: 8, background: "transparent" }} />
          </tr>
        )}
        <tr>
          <td
            onClick={hasChildren ? () => toggle(row.feature.key) : undefined}
            style={{
              padding: isChild ? `${cellPad.split(" ")[0]} 12px ${cellPad.split(" ")[0]} 32px` : cellPad,
              color: labelColor,
              fontWeight: labelWeight,
              fontSize: labelSize,
              whiteSpace: "nowrap",
              cursor: hasChildren ? "pointer" : "default",
              userSelect: hasChildren ? "none" : "auto",
              background: cellBg("feature"),
            }}
          >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{row.feature.label}</span>
              {hasChildren && (
                <i
                  className={`ti ti-chevron-${isExpanded ? "up" : "down"}`}
                  style={{ fontSize: 12, color: FG3, flexShrink: 0 }}
                />
              )}
            </span>
          </td>
          <td
            {...cellHover("activated")}
            style={{ textAlign: "center", padding: cellPad, fontSize: 12, color: labelColor, background: cellBg("activated") }}
          >
            {renderDualValue(row.reachers, total, compRow?.reachers ?? 0, compTotal, true)}
          </td>
          <td
            {...cellHover("activatedPaid")}
            style={{ textAlign: "center", padding: cellPad, fontSize: 12, color: labelColor, background: cellBg("activatedPaid") }}
          >
            {renderDualValue(row.reachersPaid, row.reachers, compRow?.reachersPaid ?? 0, compRow?.reachers ?? 0, false)}
          </td>
          <td
            {...cellHover("nonActivatedPaid")}
            style={{ textAlign: "center", padding: cellPad, fontSize: 12, color: labelColor, background: cellBg("nonActivatedPaid") }}
          >
            {renderDualValue(row.nonReachersPaid, row.nonReachers, compRow?.nonReachersPaid ?? 0, compRow?.nonReachers ?? 0, false)}
          </td>
          <td
            {...cellHover("lift")}
            style={{
              textAlign: "center", padding: cellPad, fontSize: 12, fontWeight: 600, color: labelColor,
              background: (() => {
                const heat = !compareOn && liftDefined ? liftFill(lift, isChild) : undefined;
                if (!heat) return cellBg("lift");
                return sortKey === "lift"
                  ? `linear-gradient(${ACTIVE_COL_BG}, ${ACTIVE_COL_BG}), ${heat}`
                  : heat;
              })(),
            }}
          >
            {renderDualLift(lift, liftDefined, compLift, compLiftDefined)}
          </td>
        </tr>
        {hasChildren && isExpanded && childRows[row.feature.key]?.map((c) => renderRow(c, false))}
      </Fragment>
    );
  }

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    ...(compareOn ? [{ kind: "mode" as const, label: "vs comparison" }, ...(comparePills ?? [])] : []),
    { kind: "mode" as const, label: view === "pct" ? "% of users" : "# users" },
    { kind: "mode" as const, label: `Sort: ${sortKey === "lift" ? "lift" : sortKey === "activated" ? "activated" : sortKey === "activatedPaid" ? "activated → paid" : sortKey === "nonActivatedPaid" ? "non-activated → paid" : "feature"}` },
  ];

  const expandableKeys = visibleFeatures.filter((f) => (f.children?.length ?? 0) > 0).map((f) => f.key);
  const allExpanded = expandableKeys.length > 0 && expandableKeys.every((k) => expanded.has(k));

  return (
    <section className="card" ref={cardRef} style={{ position: "relative" }}>
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Feature reach to paid conversion
            <InfoButton label="How to read this">
              <p style={{ margin: "0 0 6px" }}>
                End-state view, not weekly. For each feature: how many users in the cohort ever touched it, what
                % of those reachers are on a paid plan today, and what % of users who never touched it ended up paid.
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Lift</strong> = activated paid % ÷ non-activated paid %. A clean A-vs-B read of how much
                more (or less) likely an activated user is to be paid vs a user who never touched the feature.
                Values above 1.0× mean activated users convert at a higher rate. This is a correlation, not
                causation: paid users also tend to use more features, so a high lift can mean the feature is a
                leading indicator or just downstream of paying.
              </p>
              <p style={{ margin: 0, color: FG3 }}>
                Expand a row to see deeper thresholds (2+/5+). Use the Rows menu to hide features you don't care
                about for your read.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={`Cohort paid conversion: ${fmtPct(totalPaid, total)} (${fmtN(totalPaid)} / ${fmtN(total)} users). Lift compares activated vs non-activated paid % directly. Click a column header to sort.`}
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <div ref={rowsMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setRowsMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={rowsMenuOpen}
                className="btn btn-sm"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <i className="ti ti-list-check" style={{ fontSize: 13 }} />
                Rows
                {hiddenRows.size > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: PRIMARY,
                    background: "rgba(47,122,138,0.12)", padding: "1px 5px", borderRadius: 8,
                  }}>
                    {CONV_FEATURES.length - hiddenRows.size}/{CONV_FEATURES.length}
                  </span>
                )}
                <i className="ti ti-chevron-down" style={{ fontSize: 11, color: FG3 }} />
              </button>
              {rowsMenuOpen && (
                <div
                  role="listbox"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0,
                    background: "var(--white)", border: "1px solid var(--border-default)",
                    borderRadius: 6, boxShadow: "var(--shadow-md)", padding: 6, zIndex: 10,
                    minWidth: 220, fontSize: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 6px 6px", borderBottom: "1px solid var(--border-default)" }}>
                    <button
                      type="button"
                      onClick={() => setHiddenRows(new Set())}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: PRIMARY, fontSize: 11, fontWeight: 500 }}
                    >
                      Show all
                    </button>
                    <button
                      type="button"
                      onClick={() => setHiddenRows(new Set(CONV_FEATURES.map((f) => f.key)))}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: FG3, fontSize: 11, fontWeight: 500 }}
                    >
                      Hide all
                    </button>
                  </div>
                  {CONV_FEATURES.map((f) => {
                    const visible = !hiddenRows.has(f.key);
                    return (
                      <label
                        key={f.key}
                        style={{
                          display: "flex", alignItems: "center", gap: 8, padding: "5px 6px",
                          cursor: "pointer", borderRadius: 4,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setHiddenRows((prev) => {
                            const next = new Set(prev);
                            if (next.has(f.key)) next.delete(f.key); else next.add(f.key);
                            return next;
                          });
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => {}}
                          tabIndex={-1}
                          style={{ pointerEvents: "none" }}
                        />
                        <span style={{ color: visible ? FG1 : FG3 }}>{f.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div role="group" aria-label="Value mode" style={{
              display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11,
            }}>
              {([
                { key: "pct" as const, label: "%" },
                { key: "abs" as const, label: "#" },
              ]).map((opt) => {
                const active = view === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setView(opt.key)}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none", cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "24%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <th
                onClick={() => setSortKey("feature")}
                style={{
                  textAlign: "left", padding: "10px 12px",
                  color: sortKey === "feature" ? PRIMARY : FG3,
                  fontWeight: 600, fontSize: 11,
                  letterSpacing: "0.05em", textTransform: "uppercase",
                  cursor: "pointer", userSelect: "none",
                  background: sortKey === "feature" ? ACTIVE_COL_BG : undefined,
                  borderBottom: "1px solid var(--border-subtle, rgba(0,0,0,0.08))",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span>Feature{sortKey === "feature" ? " ▼" : ""}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpanded(allExpanded ? new Set() : new Set(expandableKeys)); }}
                    style={{
                      background: "transparent", border: "none", padding: 0, cursor: "pointer",
                      color: PRIMARY, fontSize: 10, fontWeight: 500, textDecoration: "underline",
                      letterSpacing: "0.05em", textTransform: "uppercase",
                    }}
                  >
                    {allExpanded ? "Collapse all" : "Expand all"}
                  </button>
                </span>
              </th>
              <CapsTh label="Activated" k="activated" sortKey={sortKey} onSort={setSortKey} />
              <CapsTh label="Activated → paid" k="activatedPaid" sortKey={sortKey} onSort={setSortKey} />
              <CapsTh label="Non-activated → paid" k="nonActivatedPaid" sortKey={sortKey} onSort={setSortKey} />
              <CapsTh label="Lift (activated vs non)" k="lift" sortKey={sortKey} onSort={setSortKey} />
            </tr>
          </thead>
          <tbody>
            {sortedParents.map((r, i) => renderRow(r, i === 0))}
          </tbody>
        </table>
      </div>
      {tooltip && (() => {
        const TIP_W = 300;
        const MARGIN = 14;
        const cardW = cardRef.current?.clientWidth ?? 0;
        const flipLeft = tooltip.x + MARGIN + TIP_W > cardW - 8;
        const left = flipLeft ? Math.max(8, tooltip.x - MARGIN - TIP_W) : tooltip.x + MARGIN;
        return (
          <div
            style={{
              position: "absolute", left, top: tooltip.y - 10,
              width: TIP_W, background: "var(--white)",
              border: "1px solid var(--border-default)", borderRadius: 7,
              padding: "8px 10px", fontSize: 12, pointerEvents: "none",
              boxShadow: "var(--shadow-md)", color: FG1, zIndex: 20,
              lineHeight: 1.45,
            }}
          >
            <div style={{ fontWeight: 600 }}>{tooltip.lede}</div>
            <div style={{ color: FG3, marginTop: 4 }}>{tooltip.body}</div>
            {tooltip.sub && <div style={{ color: FG3, marginTop: 4, fontSize: 11 }}>{tooltip.sub}</div>}
          </div>
        );
      })()}
    </section>
  );
}

function CapsTh({
  label, k, sortKey, onSort,
}: {
  label: string; k: SortKey; sortKey: SortKey; onSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      style={{
        textAlign: "center", padding: "10px 12px",
        color: active ? PRIMARY : FG3, fontWeight: 600, fontSize: 11,
        letterSpacing: "0.05em", textTransform: "uppercase",
        cursor: "pointer", userSelect: "none",
        background: active ? ACTIVE_COL_BG : undefined,
        borderBottom: "1px solid var(--border-subtle, rgba(0,0,0,0.08))",
      }}
    >
      {label}{active ? " ▼" : ""}
    </th>
  );
}
